import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore';
import { api } from '../../api/client';
import { RefreshCw, TrendingDown, DollarSign, Globe, Sparkles, ExternalLink } from 'lucide-react';
import { BOOST_LABELS, statusBadge } from '../../lib/pricing-labels';

interface LossRow {
  cbt_item_id: string;
  site_item_id: string;
  site_id: string;
  title: string | null;
  ptw_status: string;
  current_price: number | null;
  price_to_win: number | null;
  price_gap: number | null;
  price_gap_pct: number | null;
  currency_id: string | null;
  sold_quantity: number;
  impact_score: number;
  opportunity_boosts: string[];
  winner_item_id: string | null;
  winner_price: number | null;
  synced_at: string;
}

interface ApiEnvelope {
  success: boolean;
  data?: LossRow[];
  meta?: { count: number; generated_at: string };
  error?: string;
}

const SITES = ['all', 'MLA', 'MLB', 'MLM', 'MLC', 'MCO'];

export default function PricingLossesPage() {
  const { sellerId, setSellerId } = useUIStore();
  const [site, setSite] = useState<string>('all');
  const [minSold, setMinSold] = useState<number>(1);
  const [limit] = useState<number>(100);
  const [rows, setRows] = useState<LossRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        seller_id: String(sellerId),
        limit: String(limit),
        site,
        min_sold: String(minSold),
      });
      const res = (await api.get(`/catalog/pricing-losses?${qs.toString()}`)) as ApiEnvelope;
      if (res?.success && Array.isArray(res.data)) {
        setRows(res.data);
        setGeneratedAt(res.meta?.generated_at ?? null);
      } else {
        setRows([]);
        setError(res?.error || 'No se pudo cargar la información');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, site, minSold]);

  const stats = useMemo(() => {
    const totalItems = rows.length;
    const annualMoney = rows.reduce(
      (acc, r) => acc + Math.max(0, (r.price_gap ?? 0)) * (r.sold_quantity || 0),
      0
    );
    const countries = new Set(rows.map((r) => r.site_id)).size;
    const boostSet = new Set<string>();
    rows.forEach((r) => r.opportunity_boosts.forEach((b) => boostSet.add(b)));
    return { totalItems, annualMoney, countries, opportunities: boostSet.size };
  }, [rows]);

  const formatMoney = (n: number | null, currency?: string | null) => {
    if (n == null) return '—';
    const cur = currency || 'USD';
    return `${cur} ${Number(n).toFixed(2)}`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-text)] flex items-center gap-2">
            <TrendingDown size={22} className="text-[var(--color-error)]" />
            Dónde estás perdiendo plata
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Ranking global de productos por impacto financiero. Cuanto más se venden y más caro
            estás, más arriba aparecen.
          </p>
          {generatedAt && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Actualizado: {new Date(generatedAt).toLocaleString('es-AR')}
            </p>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--color-primary)] text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refrescar
        </button>
      </header>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-surface)]">
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">Vendedor</label>
          <select
            value={sellerId}
            onChange={(e) => setSellerId(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
          >
            <option value={1}>F5TECHNOLOGY (1)</option>
            <option value={6}>TECNO4USACORP (6)</option>
            <option value={8}>SELLING2GO (8)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">País</label>
          <select
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
          >
            {SITES.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'Todos' : s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            Vendidos mínimo
          </label>
          <input
            type="number"
            min={0}
            value={minSold}
            onChange={(e) => setMinSold(Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
          />
        </div>
        <div className="flex items-end">
          <div className="text-xs text-[var(--color-text-muted)]">
            Mostrando hasta {limit} productos ordenados por impacto.
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<TrendingDown size={18} />}
          label="Productos perdiendo"
          value={stats.totalItems.toLocaleString('es-AR')}
          color="var(--color-error)"
        />
        <StatCard
          icon={<DollarSign size={18} />}
          label="Plata en juego (estimado)"
          value={`USD ${stats.annualMoney.toFixed(0)}`}
          hint="Gap × vendidos históricos"
          color="var(--color-warning)"
        />
        <StatCard
          icon={<Globe size={18} />}
          label="Países afectados"
          value={String(stats.countries)}
          color="var(--color-primary)"
        />
        <StatCard
          icon={<Sparkles size={18} />}
          label="Oportunidades activas"
          value={String(stats.opportunities)}
          hint="Boosts disponibles"
          color="var(--color-success)"
        />
      </div>

      {/* Main table */}
      <div className="rounded-lg border border-[var(--color-border-light)] bg-[var(--color-surface)] overflow-hidden">
        {error && (
          <div className="p-4 text-sm text-[var(--color-error)] bg-[var(--color-error)]/10 border-b border-[var(--color-border-light)]">
            {error}
          </div>
        )}
        {loading && rows.length === 0 ? (
          <div className="p-8 text-center text-[var(--color-text-muted)] text-sm">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[var(--color-text-muted)] text-sm">
            🟢 Estado óptimo — no estás perdiendo en ningún site con los filtros actuales.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-bg)] border-b border-[var(--color-border-light)]">
                  <Th>País</Th>
                  <Th>Producto</Th>
                  <Th>Estado</Th>
                  <Th align="right">Tu precio</Th>
                  <Th align="right">Para ganar</Th>
                  <Th align="right">Gap</Th>
                  <Th align="right">Vendidos</Th>
                  <Th align="right">Impacto</Th>
                  <Th>Oportunidades</Th>
                  <Th>Te gana</Th>
                  <Th align="center">Acción</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const badge = statusBadge(r.ptw_status);
                  return (
                    <tr
                      key={`${r.site_item_id}`}
                      className="border-b border-[var(--color-border-light)] last:border-b-0 hover:bg-[var(--color-surface-hover)] align-top"
                    >
                      <td className="px-3 py-2 font-medium text-[var(--color-text)] whitespace-nowrap">
                        {r.site_id}
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text)] max-w-[360px]">
                        <Link
                          to={`/catalog/${r.cbt_item_id}`}
                          className="hover:underline text-[var(--color-primary)] line-clamp-2"
                          title={r.title ?? r.cbt_item_id}
                        >
                          {r.title ?? r.cbt_item_id}
                        </Link>
                        <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
                          {r.cbt_item_id} · {r.site_item_id}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ color: badge.color, background: `${badge.color}26` }}
                        >
                          {badge.emoji} {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-[var(--color-text)]">
                        {formatMoney(r.current_price, r.currency_id)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-[var(--color-text)]">
                        {formatMoney(r.price_to_win, r.currency_id)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-[var(--color-error)]">
                        {r.price_gap != null ? `${r.price_gap.toFixed(2)}` : '—'}
                        {r.price_gap_pct != null && (
                          <div className="text-[10px] text-[var(--color-text-muted)]">
                            {r.price_gap_pct.toFixed(1)}%
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-[var(--color-text)]">
                        {r.sold_quantity.toLocaleString('es-AR')}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-[var(--color-text)]">
                        {r.impact_score.toFixed(0)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.opportunity_boosts.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {r.opportunity_boosts.map((id) => (
                              <span
                                key={id}
                                className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--color-warning)]/15 text-[var(--color-warning)]"
                              >
                                {BOOST_LABELS[id] || id}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.winner_item_id ? (
                          <span className="text-[var(--color-text)]">
                            {r.winner_item_id}
                            {r.winner_price != null && (
                              <span className="text-[var(--color-text-muted)]">
                                {' '}
                                @ {formatMoney(r.winner_price, r.currency_id)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <Link
                          to={`/catalog/${r.cbt_item_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-80"
                        >
                          <ExternalLink size={12} />
                          Ver
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-medium text-[var(--color-text-muted)] text-${align}`}
    >
      {children}
    </th>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  color: string;
}) {
  return (
    <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-surface)]">
      <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] mb-1">
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-2xl font-semibold" style={{ color }}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-[var(--color-text-muted)] mt-1">{hint}</div>
      )}
    </div>
  );
}
